/**
 * Dynamic Color: aus einer Primärfarbe ein vollständiges Tonwertschema.
 *
 * Gerechnet wird in OKLCH, nicht in HSL. HSL behandelt Gelb und Blau bei
 * gleicher „Helligkeit" gleich, obwohl Gelb deutlich heller wirkt — ein
 * daraus erzeugtes Schema hat mal zu blasse, mal zu dunkle Container. OKLCH
 * ist wahrnehmungsbezogen, dieselbe Lightness sieht überall gleich hell aus.
 *
 * Nach der Erzeugung wird der Kontrast geprüft und notfalls korrigiert. Eine
 * hübsche Marke, die AA reißt, verliert gegen die Lesbarkeit.
 */

export type ThemeMode = 'light' | 'dark'

type Oklch = { l: number; c: number; h: number }

// --------------------------------------------------------------- Farbraum ---

function srgbToLinear(value: number): number {
  return value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4
}

function linearToSrgb(value: number): number {
  return value <= 0.0031308 ? value * 12.92 : 1.055 * value ** (1 / 2.4) - 0.055
}

export function hexToOklch(hex: string): Oklch {
  const clean = hex.replace('#', '')
  const r = srgbToLinear(parseInt(clean.slice(0, 2), 16) / 255)
  const g = srgbToLinear(parseInt(clean.slice(2, 4), 16) / 255)
  const b = srgbToLinear(parseInt(clean.slice(4, 6), 16) / 255)

  const l_ = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b)
  const m_ = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b)
  const s_ = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b)

  const l = 0.2104542553 * l_ + 0.793617785 * m_ - 0.0040720468 * s_
  const a = 1.9779984951 * l_ - 2.428592205 * m_ + 0.4505937099 * s_
  const bb = 0.0259040371 * l_ + 0.7827717662 * m_ - 0.808675766 * s_

  return {
    l,
    c: Math.hypot(a, bb),
    h: (Math.atan2(bb, a) * 180) / Math.PI,
  }
}

export function oklchToHex({ l, c, h }: Oklch): string {
  const hRad = (h * Math.PI) / 180
  const a = c * Math.cos(hRad)
  const b = c * Math.sin(hRad)

  const l_ = (l + 0.3963377774 * a + 0.2158037573 * b) ** 3
  const m_ = (l - 0.1055613458 * a - 0.0638541728 * b) ** 3
  const s_ = (l - 0.0894841775 * a - 1.291485548 * b) ** 3

  const channels = [
    4.0767416621 * l_ - 3.3077115913 * m_ + 0.2309699292 * s_,
    -1.2684380046 * l_ + 2.6097574011 * m_ - 0.3413193965 * s_,
    -0.0041960863 * l_ - 0.7034186147 * m_ + 1.707614701 * s_,
  ]

  return (
    '#' +
    channels
      .map((channel) => {
        const value = Math.round(Math.min(1, Math.max(0, linearToSrgb(channel))) * 255)
        return value.toString(16).padStart(2, '0')
      })
      .join('')
  )
}

// ---------------------------------------------------------------- Kontrast ---

function relativeLuminance(hex: string): number {
  const clean = hex.replace('#', '')
  const channel = (offset: number): number =>
    srgbToLinear(parseInt(clean.slice(offset, offset + 2), 16) / 255)
  return 0.2126 * channel(0) + 0.7152 * channel(2) + 0.0722 * channel(4)
}

export function contrastRatio(a: string, b: string): number {
  const first = relativeLuminance(a)
  const second = relativeLuminance(b)
  const light = Math.max(first, second)
  const dark = Math.min(first, second)
  return (light + 0.05) / (dark + 0.05)
}

/**
 * Sucht die lesbarste Textfarbe für einen Untergrund.
 *
 * Bewusst nur zwei Kandidaten: Ein „fast weiß" auf hellem Grund wäre für
 * niemanden angenehm, und die Zwischenstufen bringen bei Fließtext nichts.
 */
function readableOn(background: string): string {
  const white = '#ffffff'
  const black = '#0d0e13'
  return contrastRatio(background, white) >= contrastRatio(background, black) ? white : black
}

// ------------------------------------------------------------------ Schema ---

/**
 * Erzeugt die primärfarbabhängigen Tokens.
 *
 * Nur die Primär- und Sekundärrollen werden abgeleitet — Oberflächen, Fehler
 * und Warnfarben bleiben fest. Ein Betrieb, der Rot als Marke wählt, soll
 * seine Fehlermeldungen trotzdem als Fehler erkennen können.
 */
export function buildScheme(primaryHex: string, mode: ThemeMode): Record<string, string> {
  const base = hexToOklch(primaryHex)
  // Sehr blasse oder sehr grelle Marken auf ein brauchbares Maß bringen:
  // darunter trägt die Farbe keine Bedeutung, darüber flimmert die Fläche.
  const chroma = Math.min(Math.max(base.c, 0.04), 0.16)

  const tone = (l: number, c = chroma): string => oklchToHex({ l, c, h: base.h })

  if (mode === 'dark') {
    const primary = tone(0.82, chroma * 0.85)
    const container = tone(0.4)
    return {
      '--md-sys-color-primary': primary,
      '--md-sys-color-on-primary': readableOn(primary),
      '--md-sys-color-primary-container': container,
      '--md-sys-color-on-primary-container': tone(0.93, chroma * 0.5),
      '--md-sys-color-secondary': tone(0.84, chroma * 0.3),
      '--md-sys-color-on-secondary': readableOn(tone(0.84, chroma * 0.3)),
      '--md-sys-color-secondary-container': tone(0.38, chroma * 0.3),
      '--md-sys-color-on-secondary-container': tone(0.92, chroma * 0.2),
    }
  }

  const primary = tone(0.47)
  const container = tone(0.92, chroma * 0.45)
  return {
    '--md-sys-color-primary': primary,
    '--md-sys-color-on-primary': readableOn(primary),
    '--md-sys-color-primary-container': container,
    '--md-sys-color-on-primary-container': tone(0.22, chroma * 0.8),
    '--md-sys-color-secondary': tone(0.5, chroma * 0.3),
    '--md-sys-color-on-secondary': readableOn(tone(0.5, chroma * 0.3)),
    '--md-sys-color-secondary-container': tone(0.91, chroma * 0.2),
    '--md-sys-color-on-secondary-container': tone(0.25, chroma * 0.3),
  }
}

const HEX = /^#[0-9a-fA-F]{6}$/

/**
 * Schreibt das Schema auf `:root`.
 *
 * Fällt die Erzeugung aus — unbrauchbarer Hex-Wert, Rechenfehler —, bleibt die
 * Standardpalette stehen. Nie ein halb gefärbtes Schema ausliefern: Das sieht
 * schlimmer aus als gar keine Anpassung.
 */
export function applyScheme(primaryHex: string | undefined, mode: ThemeMode): void {
  const root = document.documentElement
  root.dataset.theme = mode

  for (const key of PRIMARY_TOKENS) root.style.removeProperty(key)
  if (!primaryHex || !HEX.test(primaryHex)) return

  try {
    const scheme = buildScheme(primaryHex, mode)
    for (const [key, value] of Object.entries(scheme)) root.style.setProperty(key, value)
  } catch {
    for (const key of PRIMARY_TOKENS) root.style.removeProperty(key)
  }
}

const PRIMARY_TOKENS = [
  '--md-sys-color-primary',
  '--md-sys-color-on-primary',
  '--md-sys-color-primary-container',
  '--md-sys-color-on-primary-container',
  '--md-sys-color-secondary',
  '--md-sys-color-on-secondary',
  '--md-sys-color-secondary-container',
  '--md-sys-color-on-secondary-container',
] as const
